import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import { tripPriceDisplay } from '@/utils/tripPriceUtils';
import { ccySym } from '@/utils/currencyUtils';
import { ownPrice as computeOwnPrice, hasNoPriceToAccept, isNegotiable, priceLabel } from '@/utils/propositionPricing';
import MatchPropositionForm from '@/pages/matching/MatchPropositionForm';
import '@/styles/matching.css';
import { cityFlag as flag } from '@/utils/cityFlag';
import { isEntreprise } from '@/utils/entreprise';
import { BACKEND_URL } from '@/config';
import VerificationBadges from '@/components/VerificationBadges';
import {
  ArrowLeft, Briefcase, CalendarDays, Inbox, Package, Send, Sparkles, Star, Tag, Trophy, Weight,
} from 'lucide-react';

const API = BACKEND_URL + '/api';
const WINE = '#722f37';
const meSide = 'carrier';
axios.defaults.withCredentials = true;

function fmtDate(d) {
  if (!d) return '·';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function ScoreBreakdown({ match }) {
  if (!match) return null;
  const items = [
    { key: 'geo',   label: 'Géographie', val: match.geo,   tone: 'gold' },
    { key: 'price', label: 'Prix',       val: match.price, tone: 'teal' },
    { key: 'time',  label: 'Temps',      val: match.time,  tone: 'navy' },
    { key: 'rep',   label: 'Réputation', val: match.rep,   tone: 'gold' },
  ];
  return (
    <div className="mt-score-breakdown">
      {items.map(i => (
        <div key={i.key} className="mt-score-cell">
          <div className={`mt-score-val mt-tone-${i.tone}`}>{i.val}</div>
          <div className="mt-score-lbl">{i.label}</div>
        </div>
      ))}
    </div>
  );
}

function ScoreDial({ total }) {
  const pct = Math.min(100, Math.max(0, total));
  const angle = (pct / 100) * 360;
  return (
    <div className="mt-dial" style={{ background: `conic-gradient(var(--gold) ${angle}deg, var(--bdl) 0deg)` }}>
      <div className="mt-dial-inner">
        <div className="mt-dial-val">{pct}</div>
        <div className="mt-dial-lbl">/ 100</div>
      </div>
    </div>
  );
}

export default function TripMatchingPage() {
  const { id: tripId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [matches, setMatches] = useState([]);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [mode, setMode] = useState('direct'); // 'direct' | 'negotiation'
  const [pendingPairSet, setPendingPairSet] = useState(new Set());

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/trips/${tripId}`),
      axios.get(`${API}/trips/${tripId}/matching?limit=30`),
      axios.get(`${API}/propositions/sent?status=PENDING&limit=50`),
    ])
      .then(([t, m, p]) => {
        setMine(t.data);
        setMatches(m.data?.data || []);
        const ids = (p.data?.data || [])
          .filter(prop => prop.trip_id === tripId)
          .map(prop => prop.shipment_id);
        setPendingPairSet(new Set(ids));
      })
      .catch(err => toast.error(err?.response?.data?.detail || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [tripId]); // eslint-disable-line react-hooks/exhaustive-deps

  const expand = async (target, nextMode) => {
    if (expandedId === target.id && mode === nextMode) { setExpandedId(null); return; }
    try {
      const fresh = await axios.get(`${API}/shipments/${target.id}`);
      const st = fresh.data?.status;
      if (!['PUBLISHED', 'PENDING_REVIEW'].includes(st)) {
        toast.error('Cette annonce colis n\'est plus disponible — elle a été fermée ou annulée.');
        setMatches(prev => prev.filter(m => m.id !== target.id));
        return;
      }
    } catch { /* réseau KO → le backend gèrera au submit */ }
    setExpandedId(target.id);
    setMode(nextMode);
  };

  if (loading) {
    return <div className="matching-page"><div className="mt-loading">Recherche des colis compatibles…</div></div>;
  }

  return (
    <div className="matching-page" data-testid="trip-matching-page">
      <button className="mt-back" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} aria-hidden="true" /> Retour
      </button>

      <div className="mt-header">
        <div>
          <h1 className="mt-title"><Sparkles size={28} aria-hidden="true" /> Matching intelligent</h1>
          {mine && (
            <div className="mt-subtitle">
              {flag(mine.departure_city)} {mine.departure_city} → {flag(mine.arrival_city)} {mine.arrival_city} · {mine.available_weight_kg}kg dispo · {tripPriceDisplay(mine).display}
            </div>
          )}
        </div>
        <div className="mt-summary">
          <div className="mt-summary-num">{matches.length}</div>
          <div className="mt-summary-lbl">colis compatible{matches.length > 1 ? 's' : ''}</div>
        </div>
      </div>

      {matches.length === 0 ? (
        <div className="mt-empty">
          <Inbox size={44} aria-hidden="true" />
          <h3>Aucun colis compatible pour l'instant</h3>
          <p>Aucune annonce publiée ne correspond à votre trajet. Revenez plus tard ou élargissez votre corridor.</p>
        </div>
      ) : (
        <div className="mt-grid">
          {matches.map((target, idx) => {
            const sender = target.sender || {};
            const m = target.match || {};
            const isExpanded = expandedId === target.id;
            const noPrice = hasNoPriceToAccept(meSide, target);
            // Ce que je gagnerais à mon tarif : au kilo × le poids du colis, ou mon forfait
            const ownPrice = computeOwnPrice(meSide, mine, target);
            const isBest = idx === 0;
            const negotiable = isNegotiable(meSide, target);
            const hasPending = pendingPairSet.has(target.id);
            return (
              <div key={target.id} className={`mt-card${isBest ? ' is-best' : ''}`} data-testid={`mt-card-${target.id}`}>
                {isBest && <div className="mt-best-flag"><Trophy size={13} aria-hidden="true" /> Meilleur match</div>}

                <div className="mt-card-head">
                  <ScoreDial total={m.total} />
                  <div className="mt-carrier">
                    <div className="mt-carrier-name">
                      {sender.first_name} {sender.last_name}
                      <VerificationBadges user={sender} compact />
                      {isEntreprise(sender) && <span className="mt-badge mt-badge-entreprise"><Briefcase size={11} aria-hidden="true" /> Entreprise</span>}
                    </div>
                    <div className="mt-carrier-meta">
                      {sender.avg_rating ? (
                        <span><Star size={12} fill="currentColor" aria-hidden="true" /> {sender.avg_rating.toFixed(1)} · {sender.rating_count || 0} avis</span>
                      ) : (
                        <span className="mt-muted">Nouveau membre</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-trip-info">
                  <div className="mt-trip-route">
                    <Package size={15} aria-hidden="true" /> {flag(target.departure_city)} {target.departure_city} → {flag(target.arrival_city)} {target.arrival_city}
                  </div>
                  <div className="mt-trip-meta">
                    <span><CalendarDays size={13} aria-hidden="true" /> {fmtDate(target.departure_date)}</span>
                    <span><Weight size={13} aria-hidden="true" /> {target.weight_kg}kg</span>
                    <span><Tag size={13} aria-hidden="true" /> {target.price_offered ? `Budget ${target.price_offered} ${ccySym(target.primary_currency || 'EUR')}` : 'Budget libre'}</span>
                  </div>
                </div>

                <ScoreBreakdown match={m} />

                <div className="mt-foot">
                  <div className="mt-price">
                    <span className="mt-price-lbl">Revenu estimé</span>
                    <span className="mt-price-amt">{ownPrice ? `${priceLabel(ownPrice.amount)} ${ccySym(ownPrice.currency)}` : 'À négocier'}</span>
                  </div>
                  <div className="mt-actions">
                    <button className="mt-btn mt-btn-ghost" onClick={() => navigate(`/app/shipments/${target.id}`)} data-testid={`mt-view-${target.id}`}>
                      Détail
                    </button>
                    {hasPending ? (
                      <div style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', background:'#fffbeb', border:'1px solid #f59e0b', borderRadius:8, fontSize:12 }}>
                        <span>⏳</span>
                        <span style={{ color:'#92400e', fontWeight:600 }}>Proposition en attente</span>
                        <button
                          onClick={() => navigate('/app/propositions')}
                          style={{ background:'none', border:'none', color:'#d97706', cursor:'pointer', textDecoration:'underline', fontSize:12, padding:0 }}
                        >
                          Gérer →
                        </button>
                      </div>
                    ) : (
                      <>
                        {!noPrice && (
                          <button
                            className={`mt-btn ${isExpanded && mode === 'direct' ? 'mt-btn-ghost' : 'mt-btn-primary'}`}
                            onClick={() => expand(target, 'direct')}
                            data-testid={`mt-propose-${target.id}`}
                          >
                            <Send size={14} aria-hidden="true" /> {isExpanded && mode === 'direct' ? 'Annuler' : 'Faire une proposition'}
                          </button>
                        )}
                        {noPrice ? (
                          <button
                            className="mt-btn"
                            style={{ background: isExpanded && mode === 'negotiation' ? '#5a2429' : WINE, color:'#fff', borderColor: WINE }}
                            onClick={() => expand(target, 'negotiation')}
                            data-testid={`mt-negotiate-${target.id}`}
                          >
                            💬 {isExpanded && mode === 'negotiation' ? 'Annuler' : 'Négocier (budget libre)'}
                          </button>
                        ) : negotiable ? (
                          <button
                            className="mt-btn"
                            style={{ background: isExpanded && mode === 'negotiation' ? '#5a2429' : WINE, color:'#fff', borderColor: WINE }}
                            onClick={() => expand(target, 'negotiation')}
                            data-testid={`mt-negotiate-${target.id}`}
                          >
                            💬 {isExpanded && mode === 'negotiation' ? 'Annuler' : 'Négocier'}
                          </button>
                        ) : (
                          <button
                            className="mt-btn mt-btn-ghost"
                            disabled
                            style={{ color:'#9ca3af', cursor:'not-allowed', borderColor:'#e5e7eb' }}
                            data-testid={`mt-non-neg-${target.id}`}
                          >
                            🔒 Non Négociable
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {isExpanded && !hasPending && (
                  <MatchPropositionForm
                    key={`${target.id}-${mode}`}
                    meSide={meSide}
                    target={target}
                    mine={mine}
                    mode={mode}
                    theirFirstName={sender.first_name}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
